import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ToastProvider";
import { AudienceSelector } from "@/components/marketing/AudienceSelector";
import { ABTestPanel } from "@/components/marketing/ABTestPanel";
import { BlockButton } from "@/components/marketing/BlockButton";
import { ContentScore } from "@/components/marketing/ContentScore";
import { cn } from "@/lib/utils";
import { mutate as mutateGlobal } from "swr";
import { api, post } from "@/lib/api";
import { schedulePost, type SchedulePostPayload } from "@/lib/scheduler";
import { PLATFORMS, PLATFORM_PERMISSION_MAP, platformLabels, type PlatformKey } from "@/lib/marketingConstants";
import { formatDateTimeInput, getSlotDateTime, type PostingWindow } from "@/lib/marketingDateUtils";
import type { ComposerAsset, ComposerState } from "@/pages/MarketingPage";
import type { AudienceSelection, MarketingPermissions, MarketingTask } from "@/types/marketing";

const CAPTION_TONES = ["mystic", "playful", "direct", "promo", "storylike"];
const EFFECT_OPTIONS = ["none", "glow", "pop", "neon"] as const;
const FILTER_OPTIONS = ["none", "grain", "vignette", "retro"] as const;

type VisualEffect = (typeof EFFECT_OPTIONS)[number];
type VisualFilter = (typeof FILTER_OPTIONS)[number];

export type PostComposerDrawerProps = {
  open: boolean;
  onClose: () => void;
  initialTask?: MarketingTask | null;
  initialAsset?: ComposerAsset | null;
  initialDatetime?: string | null;
  defaultPlatforms: PlatformKey[];
  automationWindows: PostingWindow[];
  permissions: MarketingPermissions;
  onRequestUpgrade: (message: string) => void;
  onOpenLifecycle?: (task: MarketingTask) => void;
  abTestPosts: Array<{ id: string; title: string }>;
  onBoostWithMeta?: (payload: { caption: string; mediaUrl?: string | null }) => Promise<void>;
};

/**
 * Renders the post composer drawer with platform toggles, visual controls, scheduling inputs, and composer/export flows.
 */
export function PostComposerDrawer({
  open,
  onClose,
  initialTask,
  initialAsset,
  initialDatetime,
  defaultPlatforms,
  automationWindows,
  permissions,
  onRequestUpgrade,
  onOpenLifecycle,
  abTestPosts,
  onBoostWithMeta,
}: PostComposerDrawerProps) {
  const { push } = useToast();
  const canSchedule = permissions.canSchedule;
  const canPublishNow = permissions.canPublishNow;
  const isEditing = Boolean(initialTask);
  const canViewLifecycle =
    Boolean(initialTask) && (initialTask?.status === "scheduled" || initialTask?.status === "done");
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformKey[]>(defaultPlatforms);
  const [selectedAsset, setSelectedAsset] = useState<ComposerAsset | null>(initialAsset ?? null);
  const [caption, setCaption] = useState(initialTask?.description ?? "");
  const [tone, setTone] = useState(CAPTION_TONES[0]);
  const [scheduledAt, setScheduledAt] = useState(formatDateTimeInput(initialDatetime ?? initialTask?.scheduledFor));
  const [assetTab, setAssetTab] = useState(initialAsset?.type ?? "clips");
  const [effect, setEffect] = useState<VisualEffect>(EFFECT_OPTIONS[0]);
  const [filter, setFilter] = useState<VisualFilter>(FILTER_OPTIONS[0]);
  const [savingState, setSavingState] = useState<null | "draft" | "schedule" | "post">(null);
  const [deleting, setDeleting] = useState(false);
  const [platformOptions, setPlatformOptions] = useState({
    instagram: "feed",
    tiktok: "standard",
    facebook: "post",
  });
  const [audienceTarget, setAudienceTarget] = useState<AudienceSelection>({
    cities: [],
    interests: [],
    ageRanges: [],
    platforms: defaultPlatforms,
  });
  const [boostingMeta, setBoostingMeta] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const primaryPlatform = selectedPlatforms[0] ?? defaultPlatforms[0] ?? "instagram";
  const normalizedAssetId = useMemo(() => {
    if (!selectedAsset?.id) return undefined;
    const parsed = Number(selectedAsset.id);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [selectedAsset]);
  const selectedAssetTags = selectedAsset?.tags ?? [];

  useEffect(() => {
    const desired = initialTask?.platforms ?? defaultPlatforms;
    const filtered = desired.filter((platform) => permissions.platform[PLATFORM_PERMISSION_MAP[platform]]);
    setSelectedPlatforms(filtered.length ? filtered : []);
  }, [initialTask, defaultPlatforms, permissions]);

  useEffect(() => {
    setCaption(initialTask?.description ?? "");
  }, [initialTask]);

  useEffect(() => {
    setSelectedAsset(initialAsset ?? null);
  }, [initialAsset]);

  useEffect(() => {
    setScheduledAt(formatDateTimeInput(initialDatetime ?? initialTask?.scheduledFor));
  }, [initialDatetime, initialTask]);

  useEffect(() => {
    setAudienceTarget((prev) => ({ ...prev, platforms: selectedPlatforms }));
  }, [selectedPlatforms]);

  const handleTogglePlatform = (platform: PlatformKey) => {
    const allowed = permissions.platform[PLATFORM_PERMISSION_MAP[platform]];
    if (!allowed) {
      onRequestUpgrade(`Publishing to ${platformLabels[platform] ?? platform} requires an upgrade.`);
      return;
    }
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((value) => value !== platform) : [...prev, platform],
    );
  };

  const handleAutomationWindow = () => {
    const baseDate = scheduledAt ? new Date(scheduledAt) : new Date();
    const targetWindow = automationWindows[0] ?? "morning";
    const iso = getSlotDateTime(baseDate, targetWindow);
    setScheduledAt(formatDateTimeInput(iso));
  };

  const persistPost = async (mode: "draft" | "schedule" | "post") => {
    if (mode === "schedule" && !canSchedule) {
      onRequestUpgrade("Scheduling posts requires an upgraded plan.");
      return;
    }
    if (mode === "post" && !canPublishNow) {
      onRequestUpgrade("Publishing immediately requires an upgraded plan.");
      return;
    }
    if (!selectedPlatforms.length) {
      push({ title: "Select platforms", message: "Choose at least one platform before saving.", variant: "destructive" });
      return;
    }
    setSavingState(mode);
    const hasAudience =
      audienceTarget.cities.length > 0 ||
      audienceTarget.interests.length > 0 ||
      audienceTarget.ageRanges.length > 0;
    const audiencePayload = hasAudience ? audienceTarget : undefined;

    const payload = {
      caption,
      platforms: selectedPlatforms,
      tone,
      scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      asset: selectedAsset,
      platform_options: platformOptions,
      audience: audiencePayload,
      action: mode,
    };
    try {
      if (mode === "post") {
        push({ title: "Immediate posting not yet available", message: "Use schedule to queue content.", variant: "destructive" });
        return;
      }

      if (mode === "schedule") {
        const schedulerPayload: SchedulePostPayload = {
          taskId: initialTask?.id,
          assetId: selectedAsset?.id,
          caption,
          platforms: selectedPlatforms,
          scheduledFor: scheduledAt ? new Date(scheduledAt).toISOString() : new Date().toISOString(),
          options: {
            instagramType: platformOptions.instagram === "reel" ? "reel" : "feed",
            tiktokCover: platformOptions.tiktok === "custom" ? "custom" : undefined,
            audience: audiencePayload,
          },
        };
        await schedulePost(schedulerPayload);
      } else if (isEditing && initialTask) {
        await api(`/marketing/posts/${initialTask.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await post("/marketing/posts", payload);
      }

      await Promise.all([
        mutateGlobal("/marketing/tasks"),
        mutateGlobal("/marketing/calendar"),
        mutateGlobal("/marketing/timeline"),
      ]);
      push({
        title: mode === "schedule" ? "Post scheduled" : "Post saved",
        message: mode === "schedule" ? "Publishing orchestrated via scheduler." : "Saved to the content plan.",
      });
      onClose();
    } catch (error) {
      push({
        title: "Unable to save post",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    } finally {
      setSavingState(null);
    }
  };

  const handleBoostWithMeta = async () => {
    if (!onBoostWithMeta) {
      push({ title: "Meta Ads unavailable", message: "Upgrade to unlock boosts.", variant: "destructive" });
      return;
    }
    if (!caption.trim()) {
      push({ title: "Caption needed", message: "Add a caption before boosting.", variant: "destructive" });
      return;
    }
    setBoostingMeta(true);
    try {
      await onBoostWithMeta({ caption, mediaUrl: selectedAsset?.url });
    } finally {
      setBoostingMeta(false);
    }
  };

  const handleScheduleClick = () => {
    if (!canSchedule) {
      onRequestUpgrade("Scheduling posts requires an upgraded plan.");
      return;
    }
    void persistPost("schedule");
  };

  const handlePostNowClick = () => {
    if (!canPublishNow) {
      onRequestUpgrade("Publishing immediately requires an upgraded plan.");
      return;
    }
    void persistPost("post");
  };

  const handleDelete = async () => {
    if (!initialTask) return;
    setDeleting(true);
    try {
      await api(`/marketing/posts/${initialTask.id}`, { method: "DELETE" });
      await Promise.all([
        mutateGlobal("/marketing/tasks"),
        mutateGlobal("/marketing/calendar"),
        mutateGlobal("/marketing/timeline"),
      ]);
      push({ title: "Post removed", message: "The post was unscheduled." });
      onClose();
    } catch (error) {
      push({
        title: "Unable to delete",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const renderPreview = useCallback(async () => {
    if (rendering) return exportUrl;
    setRendering(true);
    await new Promise((resolve) => setTimeout(resolve, 600));
    const url =
      selectedAsset?.url ??
      `data:text/plain,${encodeURIComponent(
        caption ? `${caption.slice(0, 100)}...` : "Post composer export",
      )}`;
    setExportUrl(url);
    setRendering(false);
    push({ title: "Render ready", message: "Preview ready for export." });
    return url;
  }, [caption, exportUrl, push, rendering, selectedAsset]);

  const handleSendToComposer = useCallback(() => {
    push({ title: "Composer ready", message: "Asset queued in Composer (placeholder)." });
    onClose();
  }, [onClose, push]);

  const selectedPlatformsSubtitle =
    selectedPlatforms.length > 0 ? selectedPlatforms.map((platform) => platformLabels[platform]).join(" • ") : "No platforms selected";

  return (
    <Sheet open={open} onOpenChange={(isOpen) => (!isOpen ? onClose() : undefined)}>
      <SheetContent className="flex w-full max-w-4xl flex-col gap-6 overflow-y-auto border-l border-border/70 bg-background/95">
        <SheetHeader className="space-y-1 text-left">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <SheetTitle className="text-2xl text-foreground">{isEditing ? "Edit Scheduled Post" : "Create New Post"}</SheetTitle>
              <p className="text-sm text-muted-foreground">{selectedPlatformsSubtitle}</p>
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              {canViewLifecycle && initialTask ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="sm:w-auto"
                  onClick={() => onOpenLifecycle?.(initialTask)}
                >
                  View lifecycle
                </Button>
              ) : null}
              <Button onClick={() => void persistPost(isEditing ? "schedule" : "draft")} disabled={Boolean(savingState)}>
                {savingState ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </SheetHeader>

        <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
          <h3 className="text-lg font-semibold text-foreground">Platforms</h3>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((platform) => {
              const allowed = permissions.platform[PLATFORM_PERMISSION_MAP[platform]];
              if (!allowed) {
                return (
                  <BlockButton
                    key={`blocked-${platform}`}
                    label={`Upgrade to publish to ${platformLabels[platform]}`}
                    onClick={() => onRequestUpgrade(`Publishing to ${platformLabels[platform]} requires an upgraded plan.`)}
                  />
                );
              }
              return (
                <label
                  key={platform}
                  className={cn(
                    "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
                    selectedPlatforms.includes(platform)
                      ? "border-success/70 bg-success/10 text-success"
                      : "border-border text-foreground/70",
                  )}
                >
                  <Checkbox
                    checked={selectedPlatforms.includes(platform)}
                    onCheckedChange={() => handleTogglePlatform(platform)}
                  />
                  {platformLabels[platform]}
                </label>
              );
            })}
          </div>
        </section>
        <AudienceSelector value={audienceTarget} onChange={setAudienceTarget} />
        <ABTestPanel availablePosts={abTestPosts} defaultPostId={initialTask?.id ?? undefined} />

        <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
          <h3 className="text-lg font-semibold text-foreground">Visual controls</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Font family
              <Select>
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue placeholder="Inter" />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  {["Inter", "Bebas Neue", "Playfair"].map((family) => (
                    <SelectItem key={family} value={family}>
                      {family}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Accent color
              <Input type="color" className="mt-1 h-11 w-full bg-background/40" defaultValue="#ffffff" />
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Text effect
              <Select value={effect} onValueChange={(value) => setEffect(value)}>
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  {EFFECT_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Filter
              <Select value={filter} onValueChange={(value) => setFilter(value)}>
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  {FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Font size
              <Input type="number" min={16} max={96} className="mt-1 bg-background/40 text-foreground" defaultValue={48} />
            </label>
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/80 bg-background/40 p-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Export</p>
            <p className="text-xs text-muted-foreground">{exportUrl ? "Latest render ready" : "Render to preview & export"}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void renderPreview()} disabled={rendering}>
              {rendering ? "Rendering…" : "Render / Export"}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                exportUrl
                  ? push({ title: "Saved", message: "Asset saved to library (placeholder)." })
                  : void renderPreview()
              }
            >
              Save as Asset
            </Button>
            <Button variant="ghost" onClick={handleSendToComposer}>
              Send to Post Composer
            </Button>
          </div>
        </div>

        <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-foreground">Asset selection</h3>
            {selectedAsset ? (
              <Badge variant="outline" className="border-border text-foreground/80">
                {selectedAsset.type === "clip" ? "Clip" : selectedAsset.type === "image" ? "Image" : "Template"} selected
              </Badge>
            ) : null}
          </div>
          {selectedAsset ? (
            <div className="rounded-xl border border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">{selectedAsset.title ?? "Selected asset"}</p>
              <p className="text-xs text-muted-foreground">{selectedAsset.type.toUpperCase()}</p>
              {selectedAsset.thumbnailUrl ? (
                <img src={selectedAsset.thumbnailUrl} alt={selectedAsset.title ?? ""} className="mt-3 aspect-video w-full rounded-lg object-cover" />
              ) : null}
              <Button size="sm" variant="outline" className="mt-3" onClick={() => setSelectedAsset(null)}>
                Choose different asset
              </Button>
            </div>
          ) : (
            <Tabs value={assetTab} onValueChange={(value) => setAssetTab(value as string)}>
              <TabsList>
                <TabsTrigger value="clips">Clips</TabsTrigger>
                <TabsTrigger value="images">Images</TabsTrigger>
                <TabsTrigger value="templates">Templates</TabsTrigger>
              </TabsList>
              <TabsContent value="clips" className="mt-3">
                <div className="grid gap-3 md:grid-cols-2">
                  {[1, 2].map((index) => (
                    <Card
                      key={index}
                      className="cursor-pointer border-border/80 bg-background/40 p-3 transition-all duration-200 hover:border-success/60"
                      onClick={() =>
                        setSelectedAsset({
                          id: `clip-${index}`,
                          type: "clip",
                          title: `Clip ${index}`,
                        })
                      }
                    >
                      <p className="text-sm font-semibold text-foreground">Suggested clip {index}</p>
                      <p className="text-xs text-muted-foreground">Tap to attach</p>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="images" className="mt-3">
                <div className="grid gap-3 md:grid-cols-2">
                  {[1, 2].map((index) => (
                    <Card
                      key={index}
                      className="cursor-pointer border-border/80 bg-background/40 p-3 transition-all duration-200 hover:border-success/60"
                      onClick={() =>
                        setSelectedAsset({
                          id: `image-${index}`,
                          type: "image",
                          title: `Image ${index}`,
                        })
                      }
                    >
                      <p className="text-sm font-semibold text-foreground">Poster concept {index}</p>
                      <p className="text-xs text-muted-foreground">Tap to attach</p>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="templates" className="mt-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <Card
                    className="cursor-pointer border-border/80 bg-background/40 p-3 transition-all duration-200 hover:border-success/60"
                    onClick={() =>
                      setSelectedAsset({
                        id: "template-story",
                        type: "template",
                        title: "Story layout",
                      })
                    }
                  >
                    <p className="text-sm font-semibold text-foreground">Story layout</p>
                    <p className="text-xs text-muted-foreground">Animated gradient overlay</p>
                  </Card>
                  <Card
                    className="cursor-pointer border-border/80 bg-background/40 p-3 transition-all duration-200 hover:border-success/60"
                    onClick={() =>
                      setSelectedAsset({
                        id: "template-square",
                        type: "template",
                        title: "Square layout",
                      })
                    }
                  >
                    <p className="text-sm font-semibold text-foreground">Square layout</p>
                    <p className="text-xs text-muted-foreground">Posterized background</p>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </section>

        <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-foreground">Caption & voice</h3>
            <Select value={tone} onValueChange={(value) => setTone(value)}>
              <SelectTrigger className="w-40 bg-background/40 text-foreground">
                <SelectValue placeholder="Tone" />
              </SelectTrigger>
              <SelectContent className="bg-background/40 text-foreground">
                {CAPTION_TONES.map((entry) => (
                  <SelectItem key={entry} value={entry}>
                    {entry}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            rows={5}
            className="w-full bg-background/40 text-foreground"
            placeholder="Tell the story, drop CTAs, or let AI assist…"
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setCaption((prev) => prev || `Auto-caption in ${tone} tone.`)}>
              Generate caption
            </Button>
            <Button variant="outline" onClick={() => setCaption(`${caption}\nVariant idea...`)}>
              Generate variants
            </Button>
            <Button variant="ghost" onClick={() => setCaption(caption.slice(0, Math.max(20, caption.length - 40)))}>
              Shorten
            </Button>
            <Button variant="ghost" onClick={() => setCaption(`${caption} More context about the drop.`)}>
              Lengthen
            </Button>
          </div>
          <ContentScore
            caption={caption}
            platform={primaryPlatform}
            assetId={normalizedAssetId}
            tags={selectedAssetTags}
            onBoost={() => {
              if (!onBoostWithMeta) return;
              void onBoostWithMeta({ caption, mediaUrl: selectedAsset?.url, budget: 75 });
            }}
          />
        </section>

        <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
          <h3 className="text-lg font-semibold text-foreground">Scheduling</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Publish at
              <Input
                type="datetime-local"
                className="mt-1 bg-background/40 text-foreground"
                value={scheduledAt}
                onChange={(event) => setScheduledAt(event.target.value)}
              />
            </label>
            <div className="flex items-end">
              <Button variant="outline" className="w-full" onClick={handleAutomationWindow}>
                Apply automation window
              </Button>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Instagram format
              <Select
                value={platformOptions.instagram}
                onValueChange={(value) => setPlatformOptions((prev) => ({ ...prev, instagram: value }))}
              >
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  <SelectItem value="feed">Feed</SelectItem>
                  <SelectItem value="reel">Reel</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              TikTok cover
              <Select
                value={platformOptions.tiktok}
                onValueChange={(value) => setPlatformOptions((prev) => ({ ...prev, tiktok: value }))}
              >
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  <SelectItem value="standard">Auto</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Facebook type
              <Select
                value={platformOptions.facebook}
                onValueChange={(value) => setPlatformOptions((prev) => ({ ...prev, facebook: value }))}
              >
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  <SelectItem value="post">Post</SelectItem>
                  <SelectItem value="reel">Reel</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </div>
        </section>

        <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
          <h3 className="text-lg font-semibold text-foreground">Previews</h3>
          <div className="grid gap-3 md:grid-cols-3">
            {selectedPlatforms.map((platform) => (
              <Card key={platform} className="border-border/60 bg-background/30 p-3 text-sm text-muted-foreground">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{platformLabels[platform]}</p>
                <div className="mt-2 h-32 rounded-lg border border-border/80 bg-background/30" />
                <p className="mt-2 text-xs text-muted-foreground">Preview based on caption + asset.</p>
              </Card>
            ))}
            {!selectedPlatforms.length && (
              <Card className="border-dashed border-border/60 bg-background/30 p-3 text-xs text-muted-foreground">
                Select platforms to preview layout.
              </Card>
            )}
          </div>
        </section>

        <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
          <h3 className="text-lg font-semibold text-foreground">Actions</h3>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void persistPost("draft")} disabled={Boolean(savingState)}>
              {savingState === "draft" ? "Saving…" : "Save draft"}
            </Button>
            <Button
              variant="secondary"
              onClick={handleScheduleClick}
              disabled={Boolean(savingState)}
              aria-disabled={!canSchedule}
            >
              {savingState === "schedule" ? "Scheduling…" : "Schedule post"}
            </Button>
            <Button
              variant="ghost"
              onClick={handlePostNowClick}
              disabled={Boolean(savingState)}
              aria-disabled={!canPublishNow}
            >
              {savingState === "post" ? "Posting…" : "Post now"}
            </Button>
            <Button variant="outline" onClick={() => void handleBoostWithMeta()} disabled={boostingMeta}>
              {boostingMeta ? "Boosting…" : "Boost via Meta Ads"}
            </Button>
            {isEditing ? (
              <Button variant="destructive" onClick={() => void handleDelete()} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete / Unschedule"}
              </Button>
            ) : null}
          </div>
        </section>
      </SheetContent>
    </Sheet>
  );
}
